import React, { type PropsWithChildren, useEffect, useRef } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';

import { useAuth } from '@sine/auth-client';

import { mapUserDetailsToIntercomProps } from './intercom.helper';

export default function WrappedIntercomProvider({ children }: PropsWithChildren<unknown>) {
  if (window.env.REACT_APP_INTERCOM_APP_ID) {
    return (
      <IntercomProvider appId={window.env.REACT_APP_INTERCOM_APP_ID}>
        <InnerIntercom>{children}</InnerIntercom>
      </IntercomProvider>
    );
  }

  return <>{children}</>;
}

function InnerIntercom({ children }: PropsWithChildren<unknown>) {
  const intercomClient = useIntercom();
  const { userDetails, authState } = useAuth();

  const intercomClientRef = useRef(intercomClient);
  const userDetailsRef = useRef(userDetails);

  intercomClientRef.current = intercomClient;
  userDetailsRef.current = userDetails;

  useEffect(() => {
    switch (authState) {
      case 'loggedIn':
        intercomClientRef.current.boot(mapUserDetailsToIntercomProps(userDetailsRef.current));
        break;
      case 'loggedOut':
        intercomClientRef.current.shutdown();
        intercomClientRef.current.boot();
        break;
      case 'loading':
      default:
        break;
    }
  }, [authState]);

  useEffect(() => {
    intercomClientRef.current.update(mapUserDetailsToIntercomProps(userDetails));
  }, [userDetails]);

  return <>{children}</>;
}
